import { Dispatch, ReactNode, SetStateAction, useCallback } from "react";
import { useUncontrolledState } from "../../hooks";
import { Dialog, DialogContent, DialogTrigger } from "../Dialog";
import { Button, ButtonProps } from "../button";
import { useTranslation } from "react-i18next";
import { Fullscreen, Minimize } from "lucide-react";
import { DialogContentProps } from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";

const STYLES = {
  content: {
    shared: "sm:max-w-[calc(100%-16px)] max-w-[calc(100%-16px)] sm:max-h-[calc(100%-16px)] max-h-[calc(100%-16px)]",
    expanded: "p-0 m-0 w-[calc(100%-16px)] h-[calc(100%-16px)] flex flex-col overflow-hidden",
    default: "overflow-hidden md:max-h-[500px] md:max-w-[700px] lg:max-w-[800px]",
  },
  expandButton: {
    button: "size-6 absolute top-[12px] right-[40px]",
    icon: "h-4 w-4",
  },
};

export interface ExpandableDialogProps extends Omit<DialogContentProps, "children"> {
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  expanded?: boolean;
  setExpanded?: Dispatch<SetStateAction<boolean>>;
  showTriggerButton?: boolean;
  triggerButtonProps?: Omit<ButtonProps, "children">;
  triggerButtonText?: string;
  showDefaultExpandButton?: boolean;
  hideCloseButton?: boolean;
  children: ReactNode;
  modal?: boolean;
}

/**
 * ExpandableDialog provides a dialog that can toggle between normal and near-fullscreen sizes.
 *
 * This component supports both controlled and uncontrolled modes for both the open state and the expanded state.
 * It includes an optional trigger button to open the dialog and an optional expand/minimize button in the header.
 *
 * @example
 * // Uncontrolled usage with default trigger button
 * <ExpandableDialog showTriggerButton triggerButtonText="Open Settings">
 *   <DialogHeader>
 *     <DialogTitle>Settings</DialogTitle>
 *   </DialogHeader>
 *   <div>Dialog content here</div>
 * </ExpandableDialog>
 *
 * @example
 * // Controlled usage with custom trigger
 * const [open, setOpen] = useState(false);
 * const [expanded, setExpanded] = useState(false);
 *
 * return (
 *   <>
 *     <Button onClick={() => setOpen(true)}>Open Dialog</Button>
 *     <ExpandableDialog open={open} setOpen={setOpen} expanded={expanded} setExpanded={setExpanded}>
 *       <DialogHeader>
 *         <DialogTitle>Custom Dialog</DialogTitle>
 *       </DialogHeader>
 *       <div>Content that adapts to expanded state</div>
 *     </ExpandableDialog>
 *   </>
 * );
 *
 * @param props - The component props
 * @param props.open - Controls whether the dialog is open. When not provided, the component manages its own state internally (defaults to false).
 * @param props.setOpen - Callback function to update the open state. When provided, the component operates in controlled mode for the open state.
 * @param props.expanded - Controls whether the dialog is expanded to near-fullscreen. When not provided, the component manages its own state internally (defaults to false).
 * @param props.setExpanded - Callback function to update the expanded state. When provided, the component operates in controlled mode for the expanded state.
 * @param props.showTriggerButton - Whether to show a default trigger button to open the dialog. Defaults to false.
 * @param props.triggerButtonProps - Props to pass to the trigger button (excludes children, which is controlled by triggerButtonText). Only applies when showTriggerButton is true.
 * @param props.triggerButtonText - Text to display on the trigger button. Falls back to translated "Open Dialog" if not provided. Only applies when showTriggerButton is true.
 * @param props.showDefaultExpandButton - Whether to show the default expand/minimize button in the dialog header. Automatically set to false when setExpanded is provided (indicating custom expand control). Defaults to true when setExpanded is not provided.
 * @param props.hideCloseButton (optional) - Hides the default close button if true
 * @param props.children - The content to render inside the dialog. Typically includes DialogHeader, DialogTitle, and other dialog content.
 * @returns A dialog component with expandable functionality
 */
export const ExpandableDialog = ({
  open: providedOpen,
  setOpen: providedSetOpen,
  expanded: providedExpanded,
  setExpanded: providedSetExpanded,
  showTriggerButton = false,
  triggerButtonProps,
  triggerButtonText: providedTriggerButtonText,
  showDefaultExpandButton = !providedSetExpanded,
  hideCloseButton = false,
  modal,
  children,
  className,
  ...contentProps
}: ExpandableDialogProps) => {
  const [open, setOpen] = useUncontrolledState({ state: providedOpen ?? false, setState: providedSetOpen });
  const [expanded, setExpanded] = useUncontrolledState({
    state: providedExpanded ?? false,
    setState: providedSetExpanded,
  });
  const shouldShowDefaultExpandButton = !providedSetExpanded && !!showDefaultExpandButton;
  const { t } = useTranslation("buttons");
  const triggerButtonText = providedTriggerButtonText ?? t("Open Dialog");

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={modal}>
      {showTriggerButton && (
        <DialogTrigger asChild>
          <Button size="sm" {...triggerButtonProps}>
            {triggerButtonText}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className={cn(
          STYLES.content.shared + (expanded ? STYLES.content.expanded : STYLES.content.default),
          hideCloseButton && "[&_[data-slot=dialog-close]]:hidden",
          className
        )}
        {...contentProps}
      >
        {children ?? (
          <div className="flex-1 bg-white rounded-lg shadow-sm border flex items-center justify-center">
            <p className="text-gray-500 text-sm">Content area</p>
          </div>
        )}
        {shouldShowDefaultExpandButton && (
          <Button variant="ghost" onClick={toggleExpanded} className={STYLES.expandButton.button}>
            {expanded ? (
              <Minimize className={STYLES.expandButton.icon} />
            ) : (
              <Fullscreen className={STYLES.expandButton.icon} />
            )}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
